import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LockIcon from '@material-ui/icons/Lock';
import Clear from '@material-ui/icons/Clear';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import {
    FormControl,
    FormHelperText,
    OutlinedInput,
    IconButton,
    Divider,
	FormControlLabel,
	Select,
	MenuItem,
} from '@material-ui/core';

import { joinClassNames } from '../../../../../../../../services/elementHelperService';
import {
    WIDGET_TYPE_BUTTON,
    WIDGET_TYPE_HOTSPOT,
    BorderRadiusKeys,
	WIDGET_TYPE_TEXT,
	transformKeys,
	TextRadiusKeys,
	WIDGET_TYPE_IFRAME,
	WIDGET_TYPE_SHAPE,
	SHAPE_TYPES_LIST
} from '../../../../../../../../constants/scenarioConstant';
import { getWidgetProps } from '../../../../../../utils/helper';
import { InteractivityProps } from './Interactivity';
import CardData from '../../../../../../utils/CardData';
import ColorPicker from '../../../../../../../../reusable/ColorPicker/ColorPicker';
import { useCustomStyles } from "../../../utils"
import TabBar from '../../../../../../utils/TabBar';
import DropShadow from './generalSettingsChild/DropShadow';
import NumberInput from './generalSettingsChild/NumberInput';
import { WidgetProps } from '../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';

const BORDER_TYPES = ['Solid', 'Dashed', 'Dotted'];

interface AppearanceProps extends InteractivityProps {
	showHoverSetting?: boolean;
	setHoverSetting: (_value: boolean) => void;
	handleBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleBGOpacityChange: (_event: {} | undefined, _color: {}) => {};
	handleHoverBGColorChange: (_event: {} | undefined, _color: {}) => {};
	handleUpdateSettings: (_settings: any) => void;
}

const Appearance = ({
	showHoverSetting = true,
    widgetTemplate: widgetTemplateProp,
	setHoverSetting,
    handleStylingAttributeChange,
	handleUpdateSettings
}: AppearanceProps) => {
    const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
    const {
        type,
		style: {
            transformSettings,
			backgroundSettings: {
				backgroundColor,
				backgroundOpacity,
				hoverBackgroundColor,
				hoverBackgroundOpacity,
				borderType,
				borderWidth,
				borderColor,
				borderOpacity,
				hoverBorderType,
				hoverBorderWidth,
				hoverBorderColor,
				hoverBorderOpacity
			},
		},
		settings: { shapeType } = {},
    } = widgetTemplate;

    const { t: translator } = useTranslation();
	const classes = useCustomStyles();
    const [isSameAllBorderRadius, setChangeAllBorderRadius] = useState(true);
	const [isSameAllPaddings, setChangeAllPaddings] = useState(true);

    const handleBorderRadiusChange = (key: string, value: string, isTextWidget: boolean) => {
        if (!isSameAllBorderRadius) {
            handleStylingAttributeChange(`transformSettings`, `${key}`, value);
            return;
        }

        handleStylingAttributeChange(`transformSettings`, isTextWidget ? 'radiusTop' : 'borderRadiusTop', value);
        handleStylingAttributeChange(`transformSettings`, isTextWidget ? 'radiusRight' : 'borderRadiusRight', value);
        handleStylingAttributeChange(`transformSettings`, isTextWidget ? 'radiusLeft' : 'borderRadiusLeft', value);
        handleStylingAttributeChange(`transformSettings`, isTextWidget ? 'radiusBottom' : 'borderRadiusBottom', value);
    };

	const handlePaddingChange = (key: string, value: string) => {
		if (!isSameAllPaddings) {
			handleStylingAttributeChange(`transformSettings`, key, value);
			return;
		}

		handleStylingAttributeChange(`transformSettings`, 'paddingTop', value);
		handleStylingAttributeChange(`transformSettings`, 'paddingRight', value);
		handleStylingAttributeChange(`transformSettings`, 'paddingLeft', value);
		handleStylingAttributeChange(`transformSettings`, 'paddingBottom', value);
	};

	const handleBGColorChange = (_event: any | undefined, color: any) => {
		handleStylingAttributeChange("backgroundSettings", "backgroundColor", color.hex);
	};
	const handleHoverBGColorChange = (_event: any | undefined, color: any) => {
		handleStylingAttributeChange("backgroundSettings", "hoverBackgroundColor", color.hex);
	};
	const handleDropShadowPropChange = (propKey: string, value: any) => {
		handleStylingAttributeChange("transformSettings", propKey, value);
	};

	const handleShapeTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		handleUpdateSettings({
			...widgetTemplate.settings,
			shapeType: (event.target.value as string).toLowerCase()
		});
	}

    return (
		<CardData title={translator('Appearance')}>
			{type === WIDGET_TYPE_SHAPE && <>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_SHAPE_TYPE')}
					onChange={event => handleShapeTypeChange(event as React.ChangeEvent<{ value: unknown }>)}
					labelPlacement="start"
					control={
						<Select variant="outlined" value={shapeType} className={classes.input}>
							{SHAPE_TYPES_LIST.map((shapeType) => (
								<MenuItem value={shapeType.toLowerCase()} key={shapeType}>
									{shapeType}
								</MenuItem>
							))}
						</Select>
					}
				/>
				<Divider className="divider-extra-space"/>
			</>}

			<div className="widget-props-wrapper">
				<div className="scene-widget__control">
					<TabBar
						size="big"
						items={[
							{
								label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_NORMAL'),
								id: 'normal',
								selected: !showHoverSetting,
							},
							{
								label: translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_HOVER'),
								id: 'hover',
								selected: showHoverSetting,
							},
						]}
						onItemClick={(_event: any, item: any) =>  setHoverSetting(item.id === 'hover')}
					/>
				</div>

				{!showHoverSetting && <>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BACKGROUND_COLOR')}
						onChange={handleBGColorChange}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<OutlinedInput value={backgroundColor} className={classes.input} />
								<ColorPicker
									width="28px"
									height="28px"
									padding="4px"
									color={backgroundColor}
									onChangeComplete={(color: any) => handleBGColorChange(undefined, color)}
								/>
								<IconButton size="small" onClick={() => handleBGColorChange(undefined, { hex: '' })}>
									<Clear />
								</IconButton>
							</div>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BACKGROUND_OPACITY')}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={backgroundOpacity}
									min={0}
									max={100}
									endAdornment="%"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'backgroundOpacity', value)}
								/>
							</div>
						}
					/>
					<Divider className="divider-extra-space" />

					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_TYPE'
						)}
						onChange={({ target }) => {
							handleStylingAttributeChange(
								`backgroundSettings`,
								'borderType',
								(target as HTMLInputElement).value.toLowerCase()
							);
						}}
						labelPlacement="start"
						control={
							<Select variant="outlined" value={borderType} className={classes.input}>
								{BORDER_TYPES.map((borderType) => (
									<MenuItem value={borderType.toLowerCase()} key={borderType}>
										{borderType}
									</MenuItem>
								))}
							</Select>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_WIDTH'
						)}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={parseInt(borderWidth)}
									min={0}
									max={100}
									endAdornment="px"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'borderWidth', value)}
								/>
							</div>}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_COLOR'
						)}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<OutlinedInput value={borderColor} className={classes.input} />
								<ColorPicker
									width="28px"
									height="28px"
									padding="4px"
									color={borderColor}
									onChangeComplete={(color: any) =>
										handleStylingAttributeChange(`backgroundSettings`, 'borderColor', color.hex)
									}
								/>
								<IconButton
									size="small"
									onClick={() =>
										handleStylingAttributeChange(`backgroundSettings`, 'borderColor', '')
									}
								>
									<Clear />
								</IconButton>
							</div>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_OPACITY'
						)}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={borderOpacity}
									min={0}
									max={100}
									endAdornment="%"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'borderOpacity', value)}
								/>
							</div>
						}
					/>

					{type !== WIDGET_TYPE_IFRAME && <>
						<Divider className="divider-extra-space" />
						<DropShadow widgetTemplate={widgetTemplate} onDropShadowPropChange={handleDropShadowPropChange} />
					</>}
				</>}

				{showHoverSetting && <>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BACKGROUND_COLOR')}
						onChange={handleHoverBGColorChange}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<OutlinedInput value={hoverBackgroundColor || backgroundColor} className={classes.input} />
								<ColorPicker
									width="28px"
									height="28px"
									padding="4px"
									color={hoverBackgroundColor || backgroundColor}
									onChangeComplete={(color: any) => handleHoverBGColorChange(undefined, color)}
								/>
								<IconButton
									size="small"
									onClick={() => handleHoverBGColorChange(undefined, { hex: '' })}
								>
									<Clear />
								</IconButton>
							</div>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_CONTENT_SETTING_BACKGROUND_OPACITY')}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={hoverBackgroundOpacity ?? backgroundOpacity}
									min={0}
									max={100}
									endAdornment="%"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'hoverBackgroundOpacity', value)}
								/>
							</div>
						}
					/>
					<Divider className="divider-extra-space" />

					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_TYPE'
						)}
						onChange={({ target }) => {
							handleStylingAttributeChange(
								`backgroundSettings`,
								'hoverBorderType',
								(target as HTMLInputElement).value
							);
						}}
						labelPlacement="start"
						control={
							<Select variant="outlined" value={hoverBorderType || borderType} className={classes.input}>
								{BORDER_TYPES.map((borderType) => (
									<MenuItem value={borderType} key={borderType}>
										{borderType}
									</MenuItem>
								))}
							</Select>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_WIDTH'
						)}
						onChange={({ target }) => {
							handleStylingAttributeChange(
								`backgroundSettings`,
								'hoverBorderWidth',
								(target as HTMLInputElement).value
							);
						}}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={(hoverBorderWidth ?? borderWidth) as any}
									min={0}
									max={100}
									endAdornment="px"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'hoverBorderWidth', value)}
								/>
							</div>}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_COLOR'
						)}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<OutlinedInput value={hoverBorderColor || borderColor} className={classes.input} />
								<ColorPicker
									width="28px"
									height="28px"
									padding="4px"
									color={hoverBorderColor || borderColor}
									onChangeComplete={(color: any) =>
										handleStylingAttributeChange(
											`backgroundSettings`,
											'hoverBorderColor',
											color.hex
										)
									}
								/>
								<IconButton
									size="small"
									onClick={() =>
										handleStylingAttributeChange(`backgroundSettings`, 'hoverBorderColor', '')
									}
								>
									<Clear />
								</IconButton>
							</div>
						}
					/>
					<FormControlLabel
						className={'scenario-chart__control'}
						label={translator(
							'SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_OPACITY'
						)}
						labelPlacement="start"
						control={
							<div className="scene-widgets__sort-input-control">
								<NumberInput
									value={(hoverBorderOpacity ?? borderOpacity) as any}
									min={0}
									max={100}
									endAdornment="%"
									onChange={(value) => handleStylingAttributeChange(`backgroundSettings`, 'hoverBorderOpacity', value)}
								/>
							</div>
						}
					/>
					{type !== WIDGET_TYPE_IFRAME && <>
						<Divider className="divider-extra-space" />
						<DropShadow widgetTemplate={widgetTemplate} onDropShadowPropChange={handleDropShadowPropChange} showHoverSetting={showHoverSetting} />
					</>}
				</>}
			</div>

            <Divider className="divider-extra-space"/>
			{(type === WIDGET_TYPE_BUTTON || type === WIDGET_TYPE_HOTSPOT || type === WIDGET_TYPE_TEXT || (type === WIDGET_TYPE_SHAPE && shapeType === 'rectangle')) && (
				<div className="scene-widget__control-column scene-widget__general-settings">
					<div className="scene-widget__control-label">
						{translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_BACKGROUND_BORDER_RADIUS')}
					</div>
					<div className="scene-widget__control-column--item">
						<div className="scene-widget__control-container">
							{Object.entries((type === WIDGET_TYPE_TEXT ? TextRadiusKeys : BorderRadiusKeys)).map(([key, displayVal], index) => {
								return (
									<FormControl
										disabled={isSameAllBorderRadius && index !== 0}
										key={key}
										className="scene-widgets__transform-input"
										variant="outlined"
									>
										<NumberInput
											value={(transformSettings as any)[key]}
											endAdornment="px"
											onChange={(value) => handleBorderRadiusChange(key, value as any, type === WIDGET_TYPE_TEXT)}
											noOnCountdown
										/>
										<FormHelperText>{displayVal}</FormHelperText>
									</FormControl>
								);
							})}
						</div>
						<IconButton
							onClick={() => setChangeAllBorderRadius(!isSameAllBorderRadius)}
							className={joinClassNames(
								'scene-widget__lock-btn',
								isSameAllBorderRadius ? 'scene-widget__lock-btn--active' : ''
							)}
						>
							{isSameAllBorderRadius ? <LockIcon /> : <LockOpenIcon />}
						</IconButton>
					</div>
				</div>
			)}

			{(type === WIDGET_TYPE_TEXT || type === WIDGET_TYPE_BUTTON) && (
				<div className="scene-widget__control-column scene-widget__general-settings">
					<div className="scene-widget__control-label">Padding</div>
					<div className="scene-widget__control-column--item">
						<div className="scene-widget__control-container">
							{Object.entries(transformKeys).map(([key, displayVal], index) => {
								return (
									<FormControl
										disabled={isSameAllPaddings && index !== 0}
										key={key}
										className="scene-widgets__transform-input"
										variant="outlined"
									>
										<NumberInput
											value={(transformSettings as any)[key]}
											endAdornment="px"
											onChange={(value) => handlePaddingChange(key, value as any)}
											noOnCountdown
										/>
										<FormHelperText>{displayVal}</FormHelperText>
									</FormControl>
								);
							})}
						</div>
						<IconButton
							onClick={() => setChangeAllPaddings(!isSameAllPaddings)}
							className={[
								'scene-widget__lock-btn',
								isSameAllPaddings ? 'scene-widget__lock-btn--active' : '',
							]
								.join(' ')
								.trim()}
						>
							{isSameAllPaddings ? <LockIcon /> : <LockOpenIcon />}
						</IconButton>
					</div>
				</div>
			)}
        </CardData>
    );
};

export default Appearance;
